import React, { memo, useMemo } from 'react';
import {
  FormPageInputItemSingleSelectionPulldown,
  SingleSelectionSelectInput,
  SingleSelectionPropertyPulldown,
} from 'shared';
import { SingleSelectionInputItemProps, SingleSelectionEvents } from './shared';

type Props = SingleSelectionInputItemProps<
  SingleSelectionPropertyPulldown,
  FormPageInputItemSingleSelectionPulldown
> & {
  value: FormPageInputItemSingleSelectionPulldown['value'];
  parentValue: FormPageInputItemSingleSelectionPulldown['value'];
} & Pick<SingleSelectionEvents, 'onSelectChange'>;

const UNSELECT_VALUE = null;

const getCandidates = (
  property: SingleSelectionPropertyPulldown,
  parentValue: Props['parentValue']
): SingleSelectionPropertyPulldown['candidates'] => {
  const { candidates, drillDown } = property;
  if (drillDown == null) {
    return candidates;
  }
  if (parentValue == null) {
    return [
      {
        value: UNSELECT_VALUE,
        label: drillDown.parentNotSelectedItemLabel,
      },
    ];
  }

  const selectableValues =
    drillDown.valuePairs.find((pair) => pair.parentValue === parentValue)
      ?.childValues ?? [];

  return candidates.filter(
    ({ value }) => value === UNSELECT_VALUE || selectableValues.includes(value)
  );
};

export const SingleSelectionPulldown: React.FC<Props> = memo(
  ({
    property,
    disabled,
    design,
    value,
    onSelectChange,
    className,
    errorMessages,
    displayError,
    parentValue,
  }) => {
    const { propertyId, inputFormat, drillDown } = property;
    const handleChange = (value: number | null) => {
      onSelectChange(value ?? undefined);
    };
    const candidates = useMemo(() => {
      return getCandidates(property, parentValue);
    }, [parentValue, property]);

    return (
      <SingleSelectionSelectInput
        propertyId={propertyId}
        displayChoiceNumber={drillDown == null && inputFormat.isNumbering}
        value={value ?? null}
        disabled={disabled}
        displayError={displayError}
        errorMessages={errorMessages}
        onSelectChange={handleChange}
        design={design}
        className={className}
        items={candidates}
      />
    );
  }
);
